import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import MemoryIcon from '@mui/icons-material/Memory';
import TimelineIcon from '@mui/icons-material/Timeline';
import api, { KRONOS } from '../../infrastructure/config/api';

function getStatusColor(status) {
  if (!status) return 'default';
  if (status.loaded) return 'success';
  if (status.available) return 'warning';
  return 'error';
}

function getStatusLabel(status) {
  if (!status) return 'Unknown';
  if (status.loaded) return 'Loaded';
  if (status.available) return 'Available, not loaded';
  return 'Unavailable';
}

function formatPrice(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  return number.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

function formatVietnamTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('vi-VN', {
    timeZone: 'Asia/Ho_Chi_Minh',
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatUtcTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toISOString().replace('.000Z', 'Z');
}

function getForecastDuration(firstItem, lastItem) {
  const start = new Date(firstItem?.timestamp).getTime();
  const end = new Date(lastItem?.timestamp).getTime();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return '-';
  const minutes = Math.round((end - start) / 60000);
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0 && remainingMinutes > 0) return `${hours}h ${remainingMinutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}

const KronosStatusPage = () => {
  const [status, setStatus] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [timeframe, setTimeframe] = useState('15m');
  const [lookback, setLookback] = useState(400);
  const [predLen, setPredLen] = useState(24);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [loadingModel, setLoadingModel] = useState(false);
  const [loadingForecast, setLoadingForecast] = useState(false);
  const [error, setError] = useState('');

  const checkStatus = useCallback(async () => {
    setLoadingStatus(true);
    setError('');
    try {
      const response = await api.get(KRONOS.STATUS);
      setStatus(response.data?.data || null);
    } catch (err) {
      setStatus(null);
      setError(err.response?.data?.error || 'Không kết nối được Kronos service');
    } finally {
      setLoadingStatus(false);
    }
  }, []);

  const loadModel = async () => {
    setLoadingModel(true);
    setError('');
    try {
      await api.post(KRONOS.LOAD_MODEL, {
        modelKey: 'kronos-small',
        device: 'cpu',
      });
      await checkStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Load Kronos model thất bại');
    } finally {
      setLoadingModel(false);
    }
  };

  const runForecast = async () => {
    setLoadingForecast(true);
    setError('');
    setForecast(null);
    try {
      const response = await api.get(KRONOS.FORECAST, {
        params: {
          symbol,
          timeframe,
          lookback,
          predLen,
        },
      });
      setForecast(response.data?.data || null);
      await checkStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Forecast bằng Kronos thất bại');
    } finally {
      setLoadingForecast(false);
    }
  };

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const firstForecast = forecast?.forecast?.[0];
  const lastForecast = forecast?.forecast?.[forecast.forecast.length - 1];
  const forecastCount = forecast?.forecast?.length || 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4">Kronos Server</Typography>
            <Typography variant="body2" color="text.secondary">
              Kiểm tra Python service port 7070 qua backend Node.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={loadingStatus ? <CircularProgress size={18} /> : <AutorenewIcon />}
            onClick={checkStatus}
            disabled={loadingStatus}
          >
            Check status
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="h6">Service status</Typography>
                  <Chip color={getStatusColor(status)} label={getStatusLabel(status)} />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Available: {status?.available ? 'yes' : 'no'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Loaded: {status?.loaded ? 'yes' : 'no'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Message: {status?.message || '-'}
                </Typography>
                {status?.current_model && (
                  <Typography variant="body2" color="text.secondary">
                    Device: {status.current_model.device || '-'}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  startIcon={loadingModel ? <CircularProgress size={18} /> : <MemoryIcon />}
                  onClick={loadModel}
                  disabled={loadingModel}
                >
                  Load kronos-small
                </Button>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography variant="h6">Test forecast</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <TextField
                    label="Symbol"
                    size="small"
                    value={symbol}
                    onChange={(e) => setSymbol(String(e.target.value || '').toUpperCase())}
                  />
                  <TextField
                    label="Timeframe"
                    size="small"
                    value={timeframe}
                    onChange={(e) => setTimeframe(e.target.value)}
                  />
                  <TextField
                    label="Lookback"
                    size="small"
                    type="number"
                    value={lookback}
                    onChange={(e) => setLookback(e.target.value)}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    label="Pred len"
                    size="small"
                    type="number"
                    value={predLen}
                    onChange={(e) => setPredLen(e.target.value)}
                    sx={{ width: 120 }}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={loadingForecast ? <CircularProgress size={18} /> : <TimelineIcon />}
                  onClick={runForecast}
                  disabled={loadingForecast}
                >
                  Run forecast
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {forecast && (
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h6">
                  Forecast result: {forecast.symbol} {forecast.timeframe}
                </Typography>
                <Chip label={`${forecastCount} candles`} variant="outlined" />
              </Box>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Forecast start (VN)</Typography>
                    <Typography variant="h6">{formatVietnamTime(firstForecast?.timestamp)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      UTC: {formatUtcTime(firstForecast?.timestamp)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Forecast end (VN)</Typography>
                    <Typography variant="h6">{formatVietnamTime(lastForecast?.timestamp)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      UTC: {formatUtcTime(lastForecast?.timestamp)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Window</Typography>
                    <Typography variant="h6">
                      {forecastCount} nến {forecast.timeframe}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Khoảng: {getForecastDuration(firstForecast, lastForecast)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Latest close</Typography>
                  <Typography variant="h6">{formatPrice(forecast.latestClose)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">First forecast close</Typography>
                  <Typography variant="h6">{formatPrice(firstForecast?.close)}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Last forecast close</Typography>
                  <Typography variant="h6">{formatPrice(lastForecast?.close)}</Typography>
                </Grid>
              </Grid>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  overflow: 'auto',
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  fontSize: 12,
                }}
              >
                {JSON.stringify({
                  inputRows: forecast.inputRows,
                  loadedNow: forecast.loadedNow,
                  meta: forecast.meta,
                  firstForecast,
                  lastForecast,
                }, null, 2)}
              </Box>
            </Stack>
          </Paper>
        )}
      </Stack>
    </Container>
  );
};

export default KronosStatusPage;
